import { SolitaireGame } from '../SolitaireGame';
import { HintItem, HintType } from './hintGroup';
import { getPlayablePyramidCards, canRemoveKing, canRemovePyramidCardPair } from '../utils/pyramid';

export class PyramidHints {
    private game: SolitaireGame;
    private static crc32Hints: number = 0;
    private static hintIndex: number = 0;

    constructor(game: SolitaireGame) {
        this.game = game;
    }

    getHint(): HintItem[] {
        console.log('[PYRAMID HINTS] Getting hint for pyramid game');
        
        const kingHints = this.findKingHint();

        const pairHints = this.findPairHint();

        const matchHints = this.findMatchHint();

        const stockPileHints = this.findInStockPile();

        const hints = this.removeStockPileDuplicates([...kingHints, ...pairHints, ...matchHints, ...stockPileHints]);
        
        const newCrc32Hints = this.getCRC32ForHints(hints.map(hint => ({
            moveGroup: hint.moveGroup.map(card => ({ code: card.code, value: card.value })),
            to: hint.to.gamePlace,
        })));

        if (newCrc32Hints === PyramidHints.crc32Hints && hints.length && hints.length > PyramidHints.hintIndex) {
            
            const index = PyramidHints.hintIndex;
            PyramidHints.hintIndex++;

            return [hints[index]];
        } else if (newCrc32Hints === PyramidHints.crc32Hints && hints.length && hints.length === PyramidHints.hintIndex) {
            PyramidHints.hintIndex = 1;
            return [hints[0]];
        }

        if (newCrc32Hints !== PyramidHints.crc32Hints && hints.length) {
            PyramidHints.hintIndex = 1;
            PyramidHints.crc32Hints = newCrc32Hints;

            return hints.length ? [hints[0]] : [];
        }
        
        
        return [];
    }

    getCRC32ForHints(arr: any[]) {
        const str = JSON.stringify(arr);
        let crc = 0xFFFFFFFF;
        for (let i = 0; i < str.length; i++) {
            crc ^= str.charCodeAt(i);
            for (let j = 0; j < 8; j++) {
            crc = (crc >>> 1) ^ (crc & 1 ? 0xEDB88320 : 0);
            }
        }
        return crc ^ 0xFFFFFFFF;
    }

    removeStockPileDuplicates(items: HintItem[]) {
        if (items.length === 0) return [];
  
        const result = [items[0]];
        
        for (let i = 1; i < items.length; i++) {
          const current = items[i];
          const prev = items[i - 1];
          
          if (current.type === HintType.MOVE_STOCKPILE && 
              prev.type === HintType.MOVE_STOCKPILE) {
            continue;
          }
          
          result.push(current);
        }
        
        return result;
      }

    private findKingHint(): HintItem[] {
        console.log('[PYRAMID HINTS] Looking for king hints');
        
        const playableCards = getPlayablePyramidCards(this.game);
        const result = [];
        
        for (const card of playableCards) {
            if (canRemoveKing(card)) {
                console.log(`[PYRAMID HINTS] Found king hint: ${card.code}`);
                const foundation = this.game.foundations[0];
                result.push(new HintItem([card], foundation, HintType.PYRAMID_KING, 1));
            }
        }

        return result;
    }

    private findPairHint(): HintItem[] {
        console.log('[PYRAMID HINTS] Looking for pair hints');
        
        const playableCards = getPlayablePyramidCards(this.game);
        const result = [];
        
        // Look for pairs that sum to 13
        for (let i = 0; i < playableCards.length; i++) {
            for (let j = i + 1; j < playableCards.length; j++) {
                const card1 = playableCards[i];
                const card2 = playableCards[j];
                
                if (canRemovePyramidCardPair(card1, card2)) {
                    console.log(`[PYRAMID HINTS] Found pair hint: ${card1.code} + ${card2.code}`);
                    const foundation = this.game.foundations[0];
                    result.push(new HintItem([card1, card2], foundation, HintType.PYRAMID_MOVE, 2));
                }
            }
        }

        return result;
    }

    private findMatchHint(): HintItem[] {
        console.log('[PYRAMID HINTS] Looking for waste pile match hints');
        
        if (!this.game.wastePile?.lastCard) return [];

        const wasteCard = this.game.wastePile.lastCard;
        const playableCards = getPlayablePyramidCards(this.game);
        const result = [];

        if (canRemoveKing(wasteCard)) {
            const foundation = this.game.foundations[0];
            result.push(new HintItem([wasteCard], foundation, HintType.PYRAMID_KING, 2));
            return result;
        }

        // Check for matches between pyramid and waste pile
        for (const pyramidCard of playableCards) {
            if (canRemovePyramidCardPair(pyramidCard, wasteCard)) {
                console.log(`[PYRAMID HINTS] Found waste pile match hint: ${pyramidCard.code} + ${wasteCard.code}`);
                const foundation = this.game.foundations[0];
                result.push(new HintItem([pyramidCard, wasteCard], foundation, HintType.PYRAMID_MOVE, 2));
            }
        }

        return result;
    }

    private findInStockPile(): HintItem[] {
        
        if (!this.game.stockPile || !this.game.wastePile) return [];

        const playableCards = getPlayablePyramidCards(this.game);
        const targetCards = [...this.game.stockPile.cards, ...this.game.wastePile.cards].filter(card => card !== this.game.wastePile?.lastCard);
        const result = [];

        for (const pyramidCard of playableCards) {
            for (const stockPileCard of targetCards) {
                if (canRemovePyramidCardPair(pyramidCard, stockPileCard)) {
                    console.log(`[PYRAMID HINTS] Found waste pile match hint: ${pyramidCard.code} + ${stockPileCard.code}`);

                    if (this.game.stockPile.cards.length) {
                        result.push(new HintItem(this.game.stockPile.cards, this.game.wastePile, HintType.MOVE_STOCKPILE, 2));
                    } else {
                        result.push(new HintItem([], this.game.stockPile, HintType.RECYCLE, 2));
                    }
                }
            }
        }
        return result;
    }
} 